import React, { useRef, useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { useDrag } from '@use-gesture/react';
import { useSprings, animated, to } from 'react-spring';
import swap from 'lodash-move';
import DraggableListContent from './DraggableListContent';
import searchPosts from '../../../../api/graphql/queries/searchPosts';

const rowHeight = 42;

const clamp = (num, cl, higher) => (higher ? Math.min(Math.max(num, cl), higher) : Math.min(num, cl));

const fn = (order, down, originalIndex, currIndex, y) => (ind) => {
  if (down && ind === originalIndex) {
    return {
      y: currIndex * rowHeight + y,
      scale: 1,
      zIndex: '1',
      shadow: 15,
      immediate: n => n === 'y' || n === 'zIndex',
    };
  }
  return {
    y: order.indexOf(ind) * rowHeight,
    scale: 1,
    zIndex: '0',
    shadow: 1,
    immediate: false,
  };
};

const initialState = { components: [] };

const itemState = '';

const DraggableListComponent = ({
  handlerSetComponentsInParent,
  handlerSetItemsPropertiesInParent,
  components,
  id,
  itemsProperties,
}) => {
  const [state, setState] = useState(initialState);
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState(components.map((_, i) => i));
  const [posts, setPosts] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  // Create springs, each corresponds to an item, controlling its transform, scale, etc.
  const [springs, springApi] = useSprings(selected.length, fn(order), [
    selected,
    order,
  ]);
  // Preps a gesture handler which returns drag-deltas, touched/clicked state, etc.

  const bind = useDrag(({ args: [originalIndex], down, movement: [, y] }) => {
    // Bunch of math to calculate current row and new order,
    const curIndex = order.indexOf(originalIndex);
    const curRow = clamp(
      Math.round((curIndex * rowHeight + y) / rowHeight),
      0,
      selected.length - 1,
    );
    const newOrder = swap(order, curIndex, curRow);
    springApi.start(fn(newOrder, down, originalIndex, curIndex, y, order));
    if (!down) {
      setOrder(newOrder);
      // reorder selected and update component on PostContainer
      const newComponentsOrder = newOrder.map(i => selected[i]);

      const newState = {
        ...state,
        components: newComponentsOrder,
      };
      setState(newState);
      handlerSetComponentsInParent(newComponentsOrder);
    }
  });

  const updateComponentInParentState = (item) => {
    const newSelected = [item, ...selected];
    // const newComponentsOrder = order.map(i => newSelected[i]);
    const newState = {
      ...state,
      components: newSelected,
    };
    setState(newState);
    setSelected(newSelected);
    handlerSetComponentsInParent(newSelected);
  };

  const deleteItemHandler = (itemIndex) => {
    const newArr = selected;
    newArr.splice(itemIndex, 1);
    const newState = {
      ...state,
      components: newArr,
    };
    setState(newState);
    const ii = order.findIndex(num => num === itemIndex);
    const n = [
      ...order.slice(0, ii),
      ...order.slice(ii + 1).map(num => (num > 0 && num > ii ? num - 1 : num)),
    ];
    setOrder(n);
    handlerSetComponentsInParent(newArr);
  };

  const searchPost = (e) => {
    const { value } = e.target;
    if (!value) {
      setSearchValue('');
      setPosts([]);
    } else {
      setSearchValue(value);
      searchPosts(value)
        .then((res) => {
          if (res === null) return setPosts([]);
          setPosts(res);
        });
    }
  };

  const addItemHandler = (item) => {
    setOrder([...order, order.length]);
    updateComponentInParentState(item);
    setPosts([]);
    setSearchValue('');
  };

  useEffect(() => {
    if (components.length) {
      setSelected(components);
      setState(components);
      setOrder(components.map((_, i) => i));
      setIsLoading(false);
      // order.current = selected.map((_, index) => index);
    }
  }, []);

  return (
    <div className="content">
      <div className="relative pl-10 mb-2">
        <div className="bg-greyFilter h-16 w-full pl-6 flex flex-col">
          <p className="text-xs uppercase pl-1 text-black font-americaMonoRegular mb-0 mt-0">Search</p>
          <input className="rounded-full pr-4 pl-9 py-2 mr-4 outline-none" type="text" onChange={searchPost} value={searchValue} />
          <span className="font-icon icon-search z-5 text-black absolute mt-8 ml-2 text-sm" />
        </div>
        <div className="absolute pin-r z-top w-2/3 pin-t">
          {
         posts && posts.length > 0 ? (
           <div className="overflow-y-auto">
             <p className="font-americaMonoBold text-xs uppercase py-1 bg-greyFilter rounded-t-lg mr-4 ml-6 pl-5 mt-3 mb-0">Available posts</p>
             <div className="max-h-150 overflow-auto bg-greyFilterItems rounded-b-lg ml-6 mr-4 py-1 mb-3">
               {
                posts.map(element => (
                  element && (
                  <div onClick={() => addItemHandler(element.slugName)} key={element.slugName}>
                    <p className="text-black font-americaMonoRegular outline-none w-full pl-6 my-1">{element.slugName }</p>
                  </div>
                  )
                ))
             }
             </div>
           </div>
         ) : null
        }
        </div>
      </div>
      <div
        className="content overflow-y-auto mb-4"
        style={{ height: selected.length * rowHeight + 50, maxHeight: '250px', minHeight: '150px' }}
      >
        {springs.map(({
          zIndex, shadow, y, scale,
        }, i) => (
          <animated.div
            {...bind(i)}
            key={i}
            style={{
              zIndex,
              // @use-gesture 10 needs this or the browser scrolls instead of dragging.
              touchAction: 'none',
              boxShadow: shadow.to(
                s => `rgba(0, 0, 0, 0.15) 0px ${s}px ${2 * s}px 0px`,
              ),
              transform: to([y], (y, s) => `translate3d(0,${y}px,0)`),
            }}
          >
            <DraggableListContent
              component={selected[i]}
              index={i}
              deleteItemHandler={deleteItemHandler}
              updateComponentInParentState={updateComponentInParentState}
              handlerSetItemsPropertiesInParent={handlerSetItemsPropertiesInParent}
              itemsProperties={itemsProperties}
            />
          </animated.div>
        ))}
      </div>
    </div>
  );
};

DraggableListComponent.propTypes = {
  handlerSetItemsPropertiesInParent: PropTypes.func.isRequired,
  itemsProperties: PropTypes.shape(),
};
DraggableListComponent.defaultProps = {
  itemsProperties: {},
};

export default DraggableListComponent;
